/**
 * Mock Auth API Client
 *
 * Provides configurable mock implementation for testing and development.
 * Returns fixtures instead of making HTTP requests.
 *
 * @module api/AuthApiClient.mock
 */

import type {
  Claims,
  Identity,
  Userinfo,
  LoginStartResponse,
  RefreshResponse,
  LogoutResponse,
} from '../types';
import type { AuthApiClient } from './AuthApiClient';
import { HttpError } from './http';

// =============================================================================
// Default Fixtures
// =============================================================================

/**
 * Default claims fixture: standard OIDC identity claims only. Under the v2 token
 * contract the ID token carries no custom platform claim — authorization comes
 * from the identity fixtures below.
 */
export const DEFAULT_CLAIMS_FIXTURE: Claims = {
  sub: 'user-123',
  iss: 'https://auth.zitadel.example.com',
  aud: 'tenant-management-ui',
  exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
  iat: Math.floor(Date.now() / 1000),
  email: 'admin@example.com',
  email_verified: true,
  name: 'Admin User',
  given_name: 'Admin',
  family_name: 'User',
  preferred_username: 'admin@example.com',
};

/**
 * Default identity fixture (`GET /api/tenant-management/me`): an authenticated
 * admin whose home org grants `platform.user.admin`.
 */
export const DEFAULT_IDENTITY_FIXTURE: Identity = {
  subject: 'user-123',
  activeOrg: { id: 'org-456', name: 'Acme Corp', primaryDomain: 'acme.example' },
  roles: ['platform.user', 'platform.user.admin'],
  isUserAdmin: true,
  isStaffAdmin: false,
  canManageMembers: true,
  canManageTenants: false,
};

/**
 * Identity fixture for an authenticated **non-admin** user: same home org, but
 * no `platform.user.admin` grant and no member-management authority.
 */
export const NON_ADMIN_IDENTITY_FIXTURE: Identity = {
  ...DEFAULT_IDENTITY_FIXTURE,
  roles: ['platform.user'],
  isUserAdmin: false,
  canManageMembers: false,
};

/**
 * Identity fixture for a ModernPath **staff admin** (home org is the vendor org,
 * carries `platform.staff.admin`).
 */
export const STAFF_IDENTITY_FIXTURE: Identity = {
  ...DEFAULT_IDENTITY_FIXTURE,
  roles: ['platform.user', 'platform.staff.admin'],
  isStaffAdmin: true,
};

/**
 * Default userinfo fixture.
 */
export const DEFAULT_USERINFO_FIXTURE: Userinfo = {
  sub: 'user-123',
  email: 'admin@example.com',
  email_verified: true,
  name: 'Admin User',
  given_name: 'Admin',
  family_name: 'User',
  preferred_username: 'admin@example.com',
  'urn:zitadel:iam:org:id': 'org-456',
  'urn:zitadel:iam:org:name': 'Acme Corp',
};

// =============================================================================
// Configuration Types
// =============================================================================

/**
 * Error configuration for a mock endpoint.
 */
export type MockErrorConfig =
  | { type: 'none' }
  | { type: '401' }
  | { type: '502' }
  | { type: 'custom'; status: number; message: string };

/**
 * Configuration options for MockAuthApiClient.
 */
export interface MockAuthApiClientConfig {
  /**
   * Claims fixture to return from getClaims().
   * @default DEFAULT_CLAIMS_FIXTURE
   */
  claims?: Claims;

  /**
   * Identity fixture to return from getIdentity().
   * @default DEFAULT_IDENTITY_FIXTURE
   */
  identity?: Identity;

  /**
   * Userinfo fixture to return from getUserInfo().
   * @default DEFAULT_USERINFO_FIXTURE
   */
  userinfo?: Userinfo;

  /**
   * Error configuration for getClaims().
   * @default { type: 'none' }
   */
  claimsError?: MockErrorConfig;

  /**
   * Error configuration for getIdentity().
   * @default { type: 'none' }
   */
  identityError?: MockErrorConfig;

  /**
   * Error configuration for getUserInfo().
   * @default { type: 'none' }
   */
  userinfoError?: MockErrorConfig;

  /**
   * Error configuration for refresh().
   * @default { type: 'none' }
   */
  refreshError?: MockErrorConfig;

  /**
   * Optional delay in milliseconds for all responses (useful for dev mode).
   * @default 0
   */
  delayMs?: number;
}

// =============================================================================
// Mock Implementation
// =============================================================================

/**
 * Mock implementation of AuthApiClient for testing and development.
 *
 * Features:
 * - Configurable claims and userinfo fixtures
 * - Configurable error responses (401, 502, custom)
 * - Optional response delay for simulating network latency
 * - No actual network requests
 *
 * @example
 * ```typescript
 * // Basic usage with defaults
 * const mockClient = new MockAuthApiClient();
 * const claims = await mockClient.getClaims();
 *
 * // Simulate unauthenticated state
 * const unauthClient = new MockAuthApiClient({
 *   claimsError: { type: '401' },
 * });
 *
 * // Custom fixtures
 * const customClient = new MockAuthApiClient({
 *   claims: { ...DEFAULT_CLAIMS_FIXTURE, email: 'custom@example.com' },
 *   delayMs: 200, // Simulate network latency
 * });
 * ```
 */
export class MockAuthApiClient implements AuthApiClient {
  private claims: Claims;
  private identity: Identity;
  private userinfo: Userinfo;
  private claimsError: MockErrorConfig;
  private identityError: MockErrorConfig;
  private userinfoError: MockErrorConfig;
  private refreshError: MockErrorConfig;
  private delayMs: number;

  /**
   * Create a new MockAuthApiClient.
   *
   * @param config - Optional configuration for fixtures and errors
   */
  constructor(config: MockAuthApiClientConfig = {}) {
    this.claims = config.claims ?? DEFAULT_CLAIMS_FIXTURE;
    this.identity = config.identity ?? DEFAULT_IDENTITY_FIXTURE;
    this.userinfo = config.userinfo ?? DEFAULT_USERINFO_FIXTURE;
    this.claimsError = config.claimsError ?? { type: 'none' };
    this.identityError = config.identityError ?? { type: 'none' };
    this.userinfoError = config.userinfoError ?? { type: 'none' };
    this.refreshError = config.refreshError ?? { type: 'none' };
    this.delayMs = config.delayMs ?? 0;
  }

  // ===========================================================================
  // Configuration Methods
  // ===========================================================================

  /**
   * Update the claims fixture.
   */
  setClaims(claims: Claims): void {
    this.claims = claims;
  }

  /**
   * Update the identity fixture.
   */
  setIdentity(identity: Identity): void {
    this.identity = identity;
  }

  /**
   * Update the userinfo fixture.
   */
  setUserinfo(userinfo: Userinfo): void {
    this.userinfo = userinfo;
  }

  /**
   * Configure getClaims() to throw an error.
   */
  setClaimsError(error: MockErrorConfig): void {
    this.claimsError = error;
  }

  /**
   * Configure getIdentity() to throw an error.
   */
  setIdentityError(error: MockErrorConfig): void {
    this.identityError = error;
  }

  /**
   * Configure getUserInfo() to throw an error.
   */
  setUserinfoError(error: MockErrorConfig): void {
    this.userinfoError = error;
  }

  /**
   * Configure refresh() to throw an error.
   */
  setRefreshError(error: MockErrorConfig): void {
    this.refreshError = error;
  }

  /**
   * Set the response delay in milliseconds.
   */
  setDelay(ms: number): void {
    this.delayMs = ms;
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  /**
   * Apply configured delay.
   */
  private async applyDelay(): Promise<void> {
    if (this.delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, this.delayMs));
    }
  }

  /**
   * Throw an HttpError based on error config.
   */
  private throwIfConfigured(errorConfig: MockErrorConfig): void {
    switch (errorConfig.type) {
      case '401':
        throw new HttpError(401, 'Unauthorized', { error: 'unauthorized' });
      case '502':
        throw new HttpError(502, 'Bad Gateway', { error: 'upstream error' });
      case 'custom':
        throw new HttpError(errorConfig.status, 'Error', { error: errorConfig.message });
      case 'none':
      default:
        // No error, continue normally
        break;
    }
  }

  // ===========================================================================
  // AuthApiClient Implementation
  // ===========================================================================

  /**
   * Returns a fake authorization URL.
   * The URL won't actually work but follows the expected format.
   */
  async loginStart(_returnTo: string): Promise<LoginStartResponse> {
    await this.applyDelay();

    const fakeAuthUrl = new URL('https://auth.zitadel.example.com/oauth/v2/authorize');
    fakeAuthUrl.searchParams.set('client_id', 'mock-client');
    fakeAuthUrl.searchParams.set('redirect_uri', 'http://localhost:5173/oauth-agent/login/callback');
    fakeAuthUrl.searchParams.set('response_type', 'code');
    fakeAuthUrl.searchParams.set('scope', 'openid profile email');
    fakeAuthUrl.searchParams.set('state', 'mock-state-' + Date.now());

    return {
      authorizationUrl: fakeAuthUrl.toString(),
    };
  }

  /**
   * Returns configured claims fixture or throws configured error.
   */
  async getClaims(): Promise<Claims> {
    await this.applyDelay();
    this.throwIfConfigured(this.claimsError);

    // Return a copy to prevent external mutation
    return { ...this.claims };
  }

  /**
   * Returns configured identity fixture or throws configured error.
   */
  async getIdentity(): Promise<Identity> {
    await this.applyDelay();
    this.throwIfConfigured(this.identityError);

    // Return a deep copy to prevent external mutation
    return {
      ...this.identity,
      activeOrg: { ...this.identity.activeOrg },
      roles: [...this.identity.roles],
    };
  }

  /**
   * Returns configured userinfo fixture or throws configured error.
   */
  async getUserInfo(): Promise<Userinfo> {
    await this.applyDelay();
    this.throwIfConfigured(this.userinfoError);

    // Return a copy to prevent external mutation
    return { ...this.userinfo };
  }

  /**
   * Returns success response or throws configured error.
   */
  async refresh(): Promise<RefreshResponse> {
    await this.applyDelay();
    this.throwIfConfigured(this.refreshError);

    return {
      loggedIn: true,
      expiresAt: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
    };
  }

  /**
   * Returns a fake end-session URL.
   * The URL won't actually work but follows the expected format.
   */
  async logout(returnTo: string): Promise<LogoutResponse> {
    await this.applyDelay();

    const fakeEndSessionUrl = new URL('https://auth.zitadel.example.com/oidc/v1/end_session');
    fakeEndSessionUrl.searchParams.set('client_id', 'mock-client');
    fakeEndSessionUrl.searchParams.set('post_logout_redirect_uri', returnTo);

    return {
      endSessionUrl: fakeEndSessionUrl.toString(),
    };
  }
}

// =============================================================================
// Convenience Factory Functions
// =============================================================================

/**
 * Create a MockAuthApiClient configured for an authenticated admin user.
 */
export function createAuthenticatedMock(overrides?: Partial<MockAuthApiClientConfig>): MockAuthApiClient {
  return new MockAuthApiClient({
    claims: DEFAULT_CLAIMS_FIXTURE,
    userinfo: DEFAULT_USERINFO_FIXTURE,
    ...overrides,
  });
}

/**
 * Create a MockAuthApiClient configured for an unauthenticated state.
 * getClaims() and getIdentity() will throw 401.
 */
export function createUnauthenticatedMock(overrides?: Partial<MockAuthApiClientConfig>): MockAuthApiClient {
  return new MockAuthApiClient({
    claimsError: { type: '401' },
    identityError: { type: '401' },
    userinfoError: { type: '401' },
    ...overrides,
  });
}

/**
 * Create a MockAuthApiClient configured for a non-admin user (identity carries
 * no `platform.user.admin`).
 */
export function createNonAdminMock(overrides?: Partial<MockAuthApiClientConfig>): MockAuthApiClient {
  return new MockAuthApiClient({
    identity: NON_ADMIN_IDENTITY_FIXTURE,
    ...overrides,
  });
}
